// Candidate for MapSelector removal

// map_selector.js
// Operations on the select2 map selector.
//
// Notes about the map selector population:
// The map selector is populated primarily by MapCase objects parsed from the
// map-list-json server response.  Structures added by the user via file upload
// or pdb import are also added to the map selector.
//
// MapCases appear in the selector under a bold heading with their "group_name".
// For cases in the map list, the group_name is the molecule name.  There are
// separate groups for PDB Imports and User Uploads, listed at the top of the
// selector, regardless of the molecule name.
//
// Under the group heading, the map cases are listed in the following form:
//      <pdbid><: ><description>
// description usually matches the TITLE line in the pdb file.
// If there is no pdbid, the molecule or gene name is used for pdbid.
// If there is a pdbid, but no description, the molecule or gene name is used
// for description.
//

import { UserActions } from 'BMapsCmds';
import { App } from './BMapsApp';
import { loadProtein } from './project_data';
import { EventBroker } from './eventbroker';
import { getColorSchemeInfo } from './redux/prefs/access';
import { MapCase, PdbImportCase, AlphaFoldImportCase } from './model/MapCase';
import { subscribe } from './redux/subscribers/stateChangeSubscriber';
import { BackgroundColors } from './themes';
import { dialogAlert } from './ui/common/DialogProvider';

const minLengthForSearch = 2;

export function initMapSelector() {
    // #mapSelectorGroup is in MapSelector.jsx
    const id = 'mapSelector';
    const gid = `${id}Group`; // group elt id = mapSelectorGroup
    const msg = $(`#${gid}`);
    msg.append(`<label id="mapSelector_label" class="mapSelector_label" for="${id}"><span id="mapSelector_label_text"></span>
                <select id="${id}" aria-hidden="true">
                   <option></option>
                </select>
                </label>`);
    console.log('Init map selector');

    EventBroker.subscribe('escapeKey', hideMapSelector);
    EventBroker.subscribe('mapSelectorCmd', (evtName, { cmd, options={} }) => {
        const { dataConnection=App.PrimaryDataConnection } = options;

        // Really we want to show the union of all mapLists from all connections when dataConnection
        // isn't defined.
        if (dataConnection) {
            updateMapSelector(dataConnection.getCaseDataCollection().getMapList(), options);
        }
        switch (cmd) {
            case 'show': showMapSelector(options); return;
            case 'hide': hideMapSelector(); return;
            case 'toggle': toggleMapSelector(options); return;
            default:
                console.log(`Unknown mapSelectorCmd: ${cmd}.`);
        }
    });

    subscribe('prefs.ColorScheme', (newScheme) => {
        updateBackgroundColor(newScheme);
    });
    updateBackgroundColor();
}

function updateBackgroundColor(scheme) {
    const colorInfo = scheme ? BackgroundColors[scheme] : getColorSchemeInfo();
    const backgroundColor = colorInfo.css + (colorInfo.style === 'dark' ? 'CC' : 'EE');
    document.getElementById('mapSelector_label').style['background-color'] = backgroundColor;
    document.getElementById('mapSelector_label_text').style['color'] = colorInfo.textCss;
}

// Reset select2 map selector to a clean state.
// Select2 is a little tricky because options are captured both in the <select>
// as well as in the select2 structure.
// Rather than trying to manage the intricacies of that, we'll just clear and rebuild.
// This is only currently called when the user connects, so should not affect user.
// If we ever allow dynamic addition of maplists, it would most likely get rebuilt
// while the selector is hidden.  How to handle if the selector is open processing new maplist?
// If the map selector is ever migrated to React, this will be unnecessary since the
// Component will always reflect the data in the datasource (maplists)
function resetMapSelector(ms) {
    if (ms.hasClass('select2-hidden-accessible')) {
        ms.select2('destroy');
    }
    ms.html('<option></option>');
    ms.off('change');
    ms.off('select2:select');
    $('.mapSelector_dropdown input.select2-search__field')
        .off('change', updateMapSelectorLabel)
        .off('input', updateMapSelectorLabel);
}

function prepareSelectItems(mapCaseList) {
    const selectItems = [];

    // Gather into groups by group name (usually same as molecule_name)
    const groups = new Map();
    // Add empty groups for structures added by user.
    // This will cause them to appear first in the map selector.
    groups.set(MapCase.GroupNames.UserUploads, []);
    groups.set(MapCase.GroupNames.PdbImports, []);
    groups.set(MapCase.GroupNames.AlphaFoldImports, []);

    for (const mapCase of mapCaseList) {
        const group = groups.get(mapCase.group_name);
        if (group) {
            group.push(mapCase);
        } else {
            groups.set(mapCase.group_name, [mapCase]);
        }
    }

    // Convert items from each group to items that will define the select items
    for (const groupItems of groups.values()) {
        if (groupItems.length > 0) {
            const groupName = groupItems[0].group_name;
            selectItems.push({
                text: groupName,
                children: groupItems.map((mapCase) => {
                    const index = mapCaseList.findIndex((x) => x === mapCase);
                    const { itemName, description, title } = displayParts(mapCase);
                    // Add things to otherSearchTerms that the select2 filter
                    // should consider.
                    const otherSearchTerms = [];
                    if (mapCase.project) otherSearchTerms.push(mapCase.project);
                    return {
                        id: index,
                        pdbid: mapCase.pdbID,
                        uppercaseSearchTerms: otherSearchTerms.map((x) => x.toUpperCase()),
                        description,
                        itemName,
                        title,
                        text: `${itemName}${itemName && description && ':  '}${description}`,
                        sourceDesc: mapCase.sourceDesc,
                        linkInfo: mapCase.myExternalLinkInfo(),
                        mapCase,
                    };
                }),
            });
        }
    }

    return selectItems;
}

/** displayParts(mapCase)
 * @description return itemName and description elements to be displayed in the
 * map selector, separated by a colon.  The logic to specify the fields depends
 * on the source of the data.
 * @returns {itemName, description}
 */
function displayParts(mapCase) {
    let itemName = mapCase.pdbID || mapCase.molecule_name || mapCase.gene_name;
    let description = mapCase.description;
    let title = ''; // For now, title is only added to user data entries to identify data source

    switch (mapCase.sourceDesc) {
        case MapCase.Sources.Public:
        case MapCase.Sources.Private:
            // "Prepared' prefix has two purposes:
            // 1) distinguish cases on which we have done full optimization and simulation
            // 2) Avoid the appearance of using pdb id to refer to our prepared data
            // That is, the referent of a pdb id should be the data in the pdb, not our data.
            // See: ftp://ftp.wwpdb.org/pub/pdb/advisory.txt
            itemName = mapCase.pdbID ? `Prepared from ${mapCase.pdbID}` : 'Prepared structure';
            break;
        case MapCase.Sources.Fragserv: {
            // For user structures, we want to display the full specified project/case names.
            // But if the project name matches the molecule name, it will already be in the
            // map selector group header, so just use the case.
            const displayName = mapCase.project === mapCase.molecule_name.replace(/ /g, '-')
                ? mapCase.case
                : mapCase.projectCase;
            itemName = `User data ${displayName}`;

            // Add title
            const dsName = mapCase?.dataSource.name;
            title = dsName ? `Source: ${dsName}` : 'Source: Custom simulations';
            if (mapCase?.lastUpdate) {
                const prefix = title ? '. ' : '';
                title += `${prefix}Last update: ${mapCase.lastUpdate.toLocaleDateString()}`;
            }
            break;
        }
        case MapCase.Sources.PDB:
            itemName = mapCase.pdbID;
            break;
        case MapCase.Sources.ImportScreen:
            break;
        case MapCase.Sources.AlphaFold:
            break;
        // no default - itemName assigned above
    }

    // Intent: if we have pdbid but no description, show mol or gene name for desc in map selector
    if (!description
        && itemName !== mapCase.molecule_name && itemName !== mapCase.gene_name) {
        description = mapCase.molecule_name || mapCase.gene_name;
    }

    return { itemName, description, title };
}

let currentMapList;
let currentSelectItems;

function openImportProtein() {
    UserActions.OpenProteinImport('import_protein_tab');
}

function updateMapSelectorLabel(evt) {
    const searchTerm = evt && evt.target.value;
    const total = currentMapList.length;
    let label = `${total} available`;
    if (searchTerm && searchTerm.length >= minLengthForSearch) {
        // I couldn't find in select2 documentation how to get the
        // search result count
        const showing = $("#select2-mapSelector-results li[role='treeitem']");
        let count = showing.length;
        if (showing.text() === 'No results found') {
            count = 0;
        }
        label = `${count} search result${count === 1 ? '' : 's'} for ${searchTerm}`;
    }
    $('#map_availability_text').text(`[${label}]`);
}

function updateMapSelector(mapList, { keepExisting, dataConnection=App.PrimaryDataConnection }={}) {
    const selectItems = prepareSelectItems(mapList);
    const placeholderText = 'Type a protein name, protein class, or PDB ID (may be DNA/RNA):';
    const label = 'Choose a protein structure to view <span id="map_availability_text"></span>';
    const uploadButton = '<button id="upload_protein_button" class="upload_button" title="Upload your own protein structure"><i class="fa fa-upload"></i> Upload</button>';
    $('#mapSelector_label_text').html(label+uploadButton);
    // Must use mousedown, since select2 will close the selector before mouseup
    $('#upload_protein_button').on('mousedown', openImportProtein).tooltipster();

    currentSelectItems = selectItems;
    currentMapList = mapList;
    const ms = $('#mapSelector');
    const select2options = {
        dropdownParent: $('#mapSelectorGroup'),
        data: selectItems,
        placeholder: placeholderText,
        width: '100%', // selector expands to the width of the parent, this also prevents label layout issue
        containerCssClass: 'mapSelector_container',
        dropdownCssClass: 'mapSelector_dropdown',
        escapeMarkup: (m) => m, // to allow markup such as an href
        allowClear: false,
        matcher: modifiedDefaultMatcher,
        templateSelection: (data) => placeholderText,
        templateResult: (data) => {
            if (data.id == null) return data.text;
            const separator = data.itemName && data.description && ': ';
            const $option = $(`<span title="${data.title || ''}">${data.itemName}${separator}${data.description}</span>`);
            const linkInfo = data.linkInfo;
            if (linkInfo) {
                const { url, label: linkLabel, title } = linkInfo;
                const $externalLink = $(
                    `<sup title="${title}">
                         <a href="${url}" target="_blank" rel="noreferrer" style="color: inherit;"> ${linkLabel}<i class="fa fa-external-link"> </i></a>
                    </sup>`
                );
                $externalLink.on('mouseup', (e) => { e.stopPropagation(); });
                $option.append($externalLink);
            }
            return $option;
        },
        tags: true,
        createTag: ({ term }) => {
            if (term.length < minLengthForSearch) return null;
            const id = term.toUpperCase().trim();
            if (PdbImportCase.isValidId(id)) {
                const { description, itemName } = PdbImportCase.newRecordMsgInfo(id);
                return {
                    id,
                    pdbid: id,
                    description,
                    itemName,
                    linkInfo: PdbImportCase.externalLinkInfo(id),
                    mapCase: new PdbImportCase(id),
                };
            } else if (AlphaFoldImportCase.isValidId(id)) {
                const { description, itemName } = AlphaFoldImportCase.newRecordMsgInfo(id);
                return {
                    id,
                    pdbid: id,
                    description,
                    itemName,
                    linkInfo: AlphaFoldImportCase.externalLinkInfo(id),
                    mapCase: new AlphaFoldImportCase(id),
                };
            }

            return null;
        },
        insertTag: (data, tag) => {
            // Always offer pdb import unless it's already in the map-list as
            // an imported PDB structure
            const match = data.find((d) => d.children.find((child) => (
                child.pdbid.toUpperCase() === tag.pdbid && child.sourceDesc === MapCase.Sources.PDB
            )));
            if (!match || match.length === 0) {
                data.push({ children: [tag] });
            }
        },
    };

    // Clear and rebuild select2 map selector every time we have an update.
    resetMapSelector(ms);
    const msdata = ms.select2(select2options).data('select2');

    ms.on('select2:select', (evt) => {
        $('#mapSelectorGroup').hide();
        // ms.select2();
        const d = evt.params.data;
        const val = ms.val();
        if (val.length === 0) return;
        const loadOptions = { keepExisting };
        const mapCase = mapList[val] || d.mapCase;
        if (mapCase) {
            const conn = App.getDataParents(mapCase).dataConnection || dataConnection;
            loadProtein({ mapCase, loadOptions, dataConnection: conn });
            ms.val(''); // uncomment this to allow reloading same protein
        } else {
            console.warn(`No case to load: ${d.pdbid}`);
        }
    });

    ms.on('select2:close', (evt) => {
        $('#mapSelectorGroup').hide();
        EventBroker.publish('mapSelectorClosed');
    });
}

function showMapSelector({ searchSeed: seed }) {
    if (currentSelectItems === undefined || currentSelectItems.length === 0) {
        dialogAlert('Select Protein', ['No protein list available yet.', 'The server may not be available.']);
        return;
    }

    $('#mapSelectorGroup').show();
    $('#mapSelector').select2('open');
    $('.mapSelector_dropdown input.select2-search__field')
        .on('change', updateMapSelectorLabel)
        .on('input', updateMapSelectorLabel);
    updateMapSelectorLabel();
    if (seed) {
        $('.mapSelector_dropdown input.select2-search__field').val(seed).trigger('input');
    }
    handleDragDropEvents();

    // Adjust max-height of mapSelector results, specific to select2
    const canvasHeight = $('#bfm_viewer').height() - 200; // subtract fudge factor
    const childHeight = $("#select2-mapSelector-results li[role='treeitem']").outerHeight();
    const nRows = Math.floor(canvasHeight / childHeight);
    const adjustedMaxHeight = childHeight*nRows;
    $('#select2-mapSelector-results').css('max-height', `${adjustedMaxHeight}px`);
}

// Since the actual select2 menu is created outside #bfm_viewer, we need to redirect
// dragdrop events to #bfm_viewer.
// Another way to do this is to make the dropdown a child of bfm_viewer using
// select2 option "dropdownParent" (the dropdown did not immediately display and
// this project is out of time)
function handleDragDropEvents() {
    const dragDropHandler = function onDrop(evt) {
        $('#bfm_viewer').trigger(evt);
        evt.preventDefault();
        return false;
    };

    $('.mapSelector_dropdown').on('drop', dragDropHandler)
        .on('dragover', dragDropHandler)
        .on('dragenter', dragDropHandler)
        .on('dragleave', dragDropHandler);
}

export function hideMapSelector() {
    if ($('#mapSelector').hasClass('select2-hidden-accessible')) { $('#mapSelector').select2('close'); }
    $('#mapSelectorGroup').hide();
    EventBroker.publish('mapSelectorClosed');
}

export function toggleMapSelector(options) {
    const visible = $('#mapSelectorGroup:visible');
    if (visible.length > 0) {
        hideMapSelector();
    } else {
        showMapSelector(options);
    }
}

// modifiedDefaultMatcher()
// This method is the default matcher from Select2, slightly rearranged.
// The uppercaseSearchTerms criterion was also added.
// Used under the MIT license
/* eslint-disable no-var */
/* eslint-disable vars-on-top */
/*
The MIT License (MIT)

Copyright (c) 2012-2015 Kevin Brown, Igor Vaynberg, and Select2 contributors

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in
all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
THE SOFTWARE.
*/
function modifiedDefaultMatcher(params, data) {
    // Always return the object if there is nothing to compare
    if ($.trim(params.term) === '') {
        return data;
    }

    var original = data.text.toUpperCase();
    var term = params.term.toUpperCase();

    // Check if the text contains the term
    if (original.indexOf(term) > -1) {
        return data;
    }

    // uppercaseSearchTerms added for Boltzmann Maps
    if (data.uppercaseSearchTerms
        && data.uppercaseSearchTerms.find((x) => x.indexOf(term) > -1)) {
        return data;
    }

    // Do a recursive check for options with children
    if (data.children && data.children.length > 0) {
        // Clone the data object if there are children
        // This is required as we modify the object to remove any non-matches
        var match = $.extend(true, {}, data);

        // Check each child of the option
        for (var c = data.children.length - 1; c >= 0; c--) {
            var child = data.children[c];

            var matches = modifiedDefaultMatcher(params, child);

            // If there wasn't a match, remove the object in the array
            if (matches == null) {
                match.children.splice(c, 1);
            }
        }

        // If any children matched, return the new object
        if (match.children.length > 0) {
            return match;
        }

        // If there were no matching children, check just the plain object
        return modifiedDefaultMatcher(params, match);
    }

    // If it doesn't contain the term, don't return anything
    return null;
}
